/**
 * <EditClientProfileModal> - Iter 9.49+
 *
 * Full client view + edit pop-up. Shows EVERY canonical field even when
 * empty (greyed "Not yet shared") and lets the host:
 *   - Edit identity (name/email/phone) while client is a stub
 *   - Tag / take private notes any time
 *   - Adjust kanban stage
 *   - DELETE the client (soft archive by default; hard delete with confirm)
 *
 * Uses PUT /api/provider/clients/{client_id} and
 *      DELETE /api/provider/clients/{client_id}
 * (Iter 9.49+ canonical CRUD). Legacy host_invite PATCH path is kept as a
 * fallback for clients with linked_user_id flow.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Phone,
  Loader2,
  Lock,
  Trash2,
  Plus,
  X,
  Sparkles,
  HeartHandshake,
  ShieldAlert,
  Salad,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => {
  const t = localStorage.getItem("session_token") || localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
};

const KANBAN_STAGES = [
  { id: "imported", label: "Imported" },
  { id: "booked", label: "Booked" },
  { id: "confirmed", label: "Confirmed" },
  { id: "completed", label: "Completed" },
  { id: "canceled", label: "Canceled" },
  { id: "no_show", label: "No show" },
];

function ReadonlyPrefList({ icon: Icon, label, items, emptyHint }) {
  return (
    <div className="border border-stone-200 rounded-lg p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-stone-700 mb-1.5">
        <Icon className="w-3 h-3 text-stone-500" />
        {label}
      </div>
      {Array.isArray(items) && items.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {items.map((it, i) => (
            <Badge key={i} variant="outline" className="text-[10px] bg-stone-50">
              {typeof it === "string" ? it : it.label || it.id}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-stone-400 italic">{emptyHint || "Not yet shared"}</p>
      )}
    </div>
  );
}

function TagChips({ tags, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-1">
      {(tags || []).map((t) => (
        <Badge
          key={t}
          variant="outline"
          className="text-[10px] bg-amber-50 border-amber-200 text-amber-800 inline-flex items-center gap-1"
        >
          {t}
          <button
            type="button"
            onClick={() => onRemove(t)}
            className="hover:text-amber-900"
            data-testid={`tag-remove-${t}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </Badge>
      ))}
      <div className="flex items-center gap-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add tag"
          className="h-6 text-[11px] w-24 px-2"
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              onAdd(draft.trim());
              setDraft("");
            }
          }}
          data-testid="tag-input"
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={() => {
            if (draft.trim()) {
              onAdd(draft.trim());
              setDraft("");
            }
          }}
          data-testid="tag-add-btn"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function EditClientProfileModal({ open, onClose, client, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({});

  const linked = !!detail?.user_id || !!client?.linked_user_id;
  // Iter 9.49+ - legacy clients use `provider_client_id`. Accept either so
  // the modal works for Acuity-imported, manually-created, and host-invited
  // rows alike.
  const resolvedId = client?.client_id || client?.provider_client_id || client?.id;

  // Hydrate full canonical client doc (with empty-field hydration on backend)
  useEffect(() => {
    if (!open || !resolvedId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/provider/clients/${resolvedId}`, { headers: headers() });
        if (res.ok) {
          const d = await res.json();
          if (cancelled) return;
          setDetail(d);
          setForm({
            first_name: d.first_name || "",
            last_name: d.last_name || "",
            email: d.email || "",
            phone: d.phone || "",
            kanban_stage: d.kanban_stage || "imported",
            host_notes: d.host_notes || "",
            client_tags: d.client_tags || [],
          });
        } else {
          // Fallback to whatever the parent passed
          setDetail(client);
          setForm({
            first_name: client.first_name || "",
            last_name: client.last_name || "",
            email: client.email || "",
            phone: client.phone || "",
            kanban_stage: client.kanban_stage || "imported",
            host_notes: client.host_notes || "",
            client_tags: client.client_tags || [],
          });
        }
      } catch {
        setDetail(client);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, client]);

  const save = async () => {
    if (!resolvedId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/provider/clients/${resolvedId}`, {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Client saved");
        onSaved?.();
        onClose?.();
      } else if (res.status === 409) {
        toast.error("Linked client - their /me profile is the source of truth.");
      } else {
        toast.error("Could not save");
      }
    } catch {
      toast.error("Network error");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!resolvedId) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      // Iter 9.49+ - hard delete so the row disappears from the directory
      // immediately. Soft archive still happens as a fallback inside the
      // delete handler (status='archived'), but `hard=true` removes the
      // physical doc so /provider/clients?event_id=... can no longer return
      // it on the next list refresh.
      const res = await fetch(`${API}/provider/clients/${resolvedId}?hard=true`, {
        method: "DELETE",
        headers: headers(),
      });
      if (res.ok) {
        toast.success("Client removed");
        onSaved?.();
        onClose?.();
      } else {
        toast.error("Could not remove");
      }
    } catch {
      toast.error("Network error");
    }
    setSaving(false);
    setConfirmDelete(false);
  };

  const d = detail || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (setConfirmDelete(false), onClose?.())}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {linked ? "Client profile" : "Edit client profile"}
            {linked && (
              <Badge variant="outline" className="text-[10px]" data-testid="edit-client-linked-badge">
                <Lock className="w-3 h-3 mr-1" /> Linked - read only
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {linked
              ? "Client has signed in - their /me profile is the source of truth for identity + preferences."
              : "Edit identity until the client signs in. Tags + private notes + kanban stage are always yours to manage."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            {/* Identity */}
            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Identity</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="First name"
                  value={form.first_name || ""}
                  onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                  disabled={linked}
                  data-testid="edit-client-first-name"
                />
                <Input
                  placeholder="Last name"
                  value={form.last_name || ""}
                  onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                  disabled={linked}
                  data-testid="edit-client-last-name"
                />
              </div>
              <div className="relative mt-2">
                <Mail className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  placeholder="Email"
                  value={form.email || ""}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  disabled={linked}
                  className="pl-8"
                  data-testid="edit-client-email"
                />
              </div>
              <div className="relative mt-2">
                <Phone className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  placeholder="Phone"
                  value={form.phone || ""}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  disabled={linked}
                  className="pl-8"
                  data-testid="edit-client-phone"
                />
              </div>
            </div>

            {/* Host fields - always editable */}
            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Kanban stage</p>
              <Select
                value={form.kanban_stage}
                onValueChange={(v) => setForm((p) => ({ ...p, kanban_stage: v }))}
              >
                <SelectTrigger className="h-9" data-testid="edit-client-kanban">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KANBAN_STAGES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Tags</p>
              <TagChips
                tags={form.client_tags}
                onAdd={(t) => setForm((p) => ({ ...p, client_tags: [...(p.client_tags || []), t] }))}
                onRemove={(t) => setForm((p) => ({ ...p, client_tags: (p.client_tags || []).filter((x) => x !== t) }))}
              />
            </div>

            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Private host notes</p>
              <Textarea
                rows={3}
                placeholder="Anything you want to remember - your client never sees this."
                value={form.host_notes || ""}
                onChange={(e) => setForm((p) => ({ ...p, host_notes: e.target.value }))}
                className="text-sm"
                data-testid="edit-client-host-notes"
              />
            </div>

            {/* Default preferences (universal, from /me Defaults) */}
            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Default preferences (from their /me)</p>
              <div className="grid grid-cols-2 gap-2">
                <ReadonlyPrefList icon={AlertTriangle} label="Allergies" items={d.default_allergies} />
                <ReadonlyPrefList icon={ShieldAlert} label="Sensitivities" items={d.default_sensitivities} />
                <ReadonlyPrefList icon={HeartHandshake} label="Accessibility" items={d.default_accessibility} />
                <ReadonlyPrefList icon={Salad} label="Dietary" items={d.default_dietary} />
              </div>
            </div>

            {/* Category-specific preferences snapshot */}
            <div>
              <p className="text-[11px] font-semibold text-stone-700 mb-1.5">
                <Sparkles className="w-3 h-3 inline mr-1 text-stone-500" />
                Category preferences (latest RSVP)
              </p>
              <div className="border border-stone-200 rounded-lg p-2.5 text-[11px]">
                {Object.keys(d.makeup_preferences || {}).length > 0 ? (
                  <div className="space-y-0.5">
                    {Object.entries(d.makeup_preferences || {}).slice(0, 8).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2">
                        <span className="text-stone-500 capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="text-stone-800 truncate max-w-[60%]">
                          {Array.isArray(v) ? v.join(", ") : String(v || "")}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-400 italic">Client has not completed any preferences yet.</p>
                )}
              </div>
            </div>

            {/* Source + history */}
            <div className="grid grid-cols-2 gap-2 text-[10px] text-stone-500 pt-1 border-t border-stone-200">
              <div>
                <span className="text-stone-400">Source:</span> {d.imported_from || "manual"}
              </div>
              <div className="text-right">
                <span className="text-stone-400">Visits:</span> {d.visit_count || 0}
              </div>
              {d.last_appointment_at && (
                <div className="col-span-2">
                  <span className="text-stone-400">Last:</span> {d.last_service_name || "(service unknown)"} - {new Date(d.last_appointment_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-3 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={remove}
            disabled={saving}
            className={confirmDelete ? "bg-red-50 text-red-700 hover:bg-red-100" : "text-red-600 hover:bg-red-50"}
            data-testid="edit-client-delete"
          >
            <Trash2 className="w-3 h-3 mr-1.5" />
            {confirmDelete ? "Confirm remove" : "Remove client"}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => onClose?.()} data-testid="edit-client-cancel">
            Close
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-stone-900 hover:bg-stone-800 text-white"
            data-testid="edit-client-save"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
